<template>
  <div class="pa-4 findings-page">
    <!-- Header -->
    <div class="ww-page-header findings-page-header">
      <div class="ww-page-heading">
        <div class="ww-page-title">Findings</div>
        <div class="ww-page-subtitle">View and manage all of your findings</div>
      </div>
      <div class="d-flex ga-2">
        <v-btn color="success" variant="outlined" @click="exportToExcel" :loading="exporting">
          <v-icon start icon="mdi-file-excel" /> Export to Excel
        </v-btn>
        <SecureButton
          form="Findings_Grid"
          control="Create"
          color="primary"
          @click="onAddFinding"
        >
          <v-icon start icon="mdi-plus" /> Create Finding
        </SecureButton>
      </div>
    </div>

    <!-- Filters -->
    <div class="d-flex ga-3 mb-4 flex-wrap align-center findings-filters">
      <v-select
        v-model="statusFilter"
        :items="findingStatuses"
        item-title="name"
        item-value="lookupDataId"
        label="Filter Status"
        clearable
        density="comfortable"
        style="max-width: 200px"
      />
      <v-select
        v-model="zoneFilter"
        :items="zones"
        item-title="name"
        item-value="name"
        label="Filter Zone"
        clearable
        density="comfortable"
        style="max-width: 200px"
      />
      <v-select
        v-model="formFilter"
        :items="surveyTypes"
        label="Filter Survey Type"
        clearable
        density="comfortable"
        style="max-width: 220px"
      />
      <v-spacer />
      <v-text-field
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        placeholder="Search findings"
        density="comfortable"
        clearable
        style="max-width: 300px"
      />
    </div>

    <!-- Grid -->
    <v-card class="findings-grid-card" elevation="1">
    <v-data-table
      :headers="headers"
      :items="filteredFindings"
      :loading="loading"
      class="elevation-0 findings-table"
      item-key="findingId"
      density="comfortable"
      loading-text="Loading findings…"
      no-data-text="No findings found."
      height="500"
      fixed-header
      :sort-by="[{ key: 'submitTime', order: 'desc' }]"
      @click:row="onRowClick"
    >
      <template #item.title="{ item }">
        <span class="finding-title-link">{{ item.details || item.title }}</span>
      </template>

      <template #item.submittedByFK="{ item }">
        <div class="submitted-by-cell">
          <v-avatar size="32" class="submitted-by-avatar bg-avatar">
            <v-img :src="getUserIconUrl(item.ownerIconId)" />
          </v-avatar>
          <div class="submitted-by-text">
            {{ getUserDisplayName(item) }}
          </div>
        </div>
      </template>

      <template #item.submitTime="{ item }">
        {{ formatDateTime(item.submitTime) }}
      </template>

      <!-- The survey/form the finding was captured on (Survey_Forms.Name). -->
      <template #item.formName="{ item }">
        <div v-if="item.formName" class="survey-type-cell">
          <v-icon size="16" :color="item.iconColour || 'grey-darken-1'">
            mdi-clipboard-text-outline
          </v-icon>
          <span class="survey-type-text">{{ item.formName }}</span>
        </div>
        <span v-else class="text-medium-emphasis">—</span>
      </template>

      <template #item.priorityFK="{ item }">
        <v-chip
          v-if="item.priorityFK"
          :color="item.priorityColour || '#9e9e9e'"
          variant="elevated"
          size="small"
          text-color="white"
        >
          {{ item.priorityFK }}
        </v-chip>
        <v-chip v-else color="grey" variant="elevated" size="small">
          No Priority
        </v-chip>
      </template>

      <template #item.statusFK="{ item }">
        <v-chip
          v-if="item.statusFK && item.statusColour"
          :color="item.statusColour.toLowerCase()"
          variant="elevated"
          size="small"
        >
          {{ item.statusFK }}
        </v-chip>
        <v-chip v-else color="grey" variant="elevated" size="small">
          No Status Set
        </v-chip>
      </template>

      <template #item.photoReviewStatus="{ item }">
        <v-chip size="small" variant="tonal" :color="photoReviewColor(item.photoReviewStatus)" prepend-icon="mdi-image-check-outline">
          {{ item.photoReviewStatus }}<span v-if="item.photoCount"> ({{ item.photoCount }})</span>
        </v-chip>
      </template>

      <template #item.tasks="{ item }">
        <v-tooltip :text="taskCountFor(item) === 0 ? 'No tasks' : `View ${taskCountFor(item)} task(s)`" location="top">
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              variant="text"
              min-width="32"
              height="32"
              :class="['task-badge-btn', { 'task-badge--zero': taskCountFor(item) === 0 || taskCountFor(item) === '…' }]"
              :disabled="taskCountFor(item) === 0 || taskCountFor(item) === '…'"
              @click.stop="taskCountFor(item) > 0 && goToTasks(item)"
            >
              <span class="task-badge">{{ taskCountFor(item) }}</span>
            </v-btn>
          </template>
        </v-tooltip>
      </template>

      <template #item.actions="{ item }">
        <div class="d-flex align-center ga-1" @click.stop>
          <v-btn icon size="x-small" @click.stop="editFinding(item.findingId || item.FindingId)">
            <v-icon size="20">mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon size="x-small" color="error" @click.stop="confirmDeleteFinding(item)">
            <v-icon size="20">mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>
    </v-card>
  </div>

  <!-- Delete Confirmation Dialog -->
  <v-dialog v-model="showDeleteConfirm" max-width="500px" persistent>
    <v-card>
      <v-card-title class="text-error d-flex align-center">
        <v-icon class="mr-2">mdi-alert</v-icon>
        Confirm Delete
      </v-card-title>
      <v-divider />
      <v-card-text class="pt-4">
        <p class="text-body-1">
          Are you sure you want to delete <strong>"{{ findingToDelete?.title }}"</strong>?
        </p>
        <p class="text-body-2 text-grey mt-2">
          This action cannot be undone. The finding will be permanently removed.
        </p>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="showDeleteConfirm = false" :disabled="deletingFinding">Cancel</v-btn>
        <v-btn color="error" variant="flat" @click="deleteFinding" :loading="deletingFinding">Delete</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { getAll, deleteItem, exportFindingsToExcel } from "@/api/findings";
import { getLookupDataByType } from "@/api/lookups";
import { fetchZones } from "@/api/zones";
import { fetchUsers } from "@/api/dropdowns";
import SecureButton from "@/components/SecureButton.vue";

export default {
  name: "FindingList",
  components: { SecureButton },
  data() {
    return {
      headers: [
        { title: "Description", key: "title" },
        { title: "Zone", key: "zoneNameFK" },
        { title: "Survey Type", key: "formName" },
        { title: "Priority", key: "priorityFK" },
        { title: "Submitted By", key: "submittedByFK" },
        {
          title: "Submitted",
          key: "submitTime",
          value: (item) => {
            if (!item.submitTime) return 0;
            const date = new Date(item.submitTime);
            return isNaN(date.getTime()) ? 0 : date.getTime();
          },
        },
        { title: "Status", key: "statusFK" },
        { title: "Photo Review", key: "photoReviewStatus" },
        { title: "Tasks", key: "tasks", sortable: false, width: 80, align: "center" },
        { title: "Actions", key: "actions", sortable: false },
      ],
      taskCounts: {},
      findings: [],
      loading: false,
      exporting: false,
      search: "",
      statusFilter: null,
      zoneFilter: null,
      formFilter: null,
      findingStatuses: [],
      zones: [],
      users: [],
      showDeleteConfirm: false,
      findingToDelete: null,
      deletingFinding: false,
    };
  },
  computed: {
    // Survey types are derived from the loaded findings, so the filter needs no extra call
    // and only ever offers types that actually appear in the data.
    surveyTypes() {
      return [...new Set(this.findings.map((f) => f.formName).filter(Boolean))].sort((a, b) =>
        String(a).localeCompare(String(b))
      );
    },
    filteredFindings() {
      return this.findings.filter((f) => {
        if (this.statusFilter && f.statusId !== this.statusFilter) return false;
        if (this.zoneFilter && f.zoneNameFK !== this.zoneFilter) return false;
        if (this.formFilter && f.formName !== this.formFilter) return false;
        if (this.search) {
          const term = this.search.toLowerCase();
          if (!Object.values(f).some((val) => String(val).toLowerCase().includes(term))) return false;
        }
        return true;
      });
    },
  },
  methods: {
    photoReviewColor(status) {
      return ({ "Reviewed": "success", "Partially reviewed": "info", "Review required": "warning", "Analysis pending": "blue", "Not analysed": "grey", "No photos": "grey-lighten-1" })[status] || "grey";
    },
    async exportToExcel() {
      this.exporting = true;
      try {
        const response = await exportFindingsToExcel();
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Findings_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$toast?.success?.("Findings exported successfully");
      } catch (error) {
        this.$toast?.error?.("Failed to export findings");
      } finally {
        this.exporting = false;
      }
    },
    async fetchFindings() {
      this.loading = true;
      try {
        const resp = await getAll();
        this.findings = resp.map((i) => ({ ...i }));
        // Server now returns taskCount on each finding (FindingDto.TaskCount).
        // Cache from the response so taskCountFor() works without firing
        // per-row HTTP requests — the old loadTaskCounts() was an N+1
        // that hit ERR_INSUFFICIENT_RESOURCES on busy customers.
        this.taskCounts = {};
        for (const f of this.findings) {
          const id = f.findingId || f.FindingId;
          if (id != null) {
            this.taskCounts[id] = f.taskCount ?? f.TaskCount ?? 0;
          }
        }
      } catch (err) {
        // handled silently
      } finally {
        this.loading = false;
      }
    },
    taskCountFor(item) {
      const id = item.findingId || item.FindingId;
      return id ? (this.taskCounts[id] ?? 0) : 0;
    },
    goToTasks(item) {
      const id = item.findingId || item.FindingId;
      if (id) this.$router.push({ path: "/tasks", query: { findingId: id } });
    },
    async loadFindingStatuses() {
      try {
        const result = await getLookupDataByType("FindingStatus");
        this.findingStatuses = result
          .filter((item) => item.isActive)
          .map((item) => ({ lookupDataId: item.lookupDataId, name: item.name }));
      } catch {
        this.findingStatuses = [];
      }
    },
    async loadZones() {
      try {
        this.zones = await fetchZones();
      } catch {
        this.zones = [];
      }
    },
    async loadUsers() {
      try {
        this.users = await fetchUsers();
      } catch {
        this.users = [];
      }
    },
    isGuidLike(value) {
      return typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
    },
    getUserDisplayName(item) {
      const directName = item?.submittedByFK || item?.submittedByName || item?.ownerName;
      if (directName && !this.isGuidLike(directName)) {
        return directName;
      }

      const userId = item?.submittedBy || item?.ownerId || item?.userId;
      const matchedUser = (this.users || []).find((u) => {
        const id = u.id || u.userId || u.Id || u.UserId;
        return id && String(id) === String(userId);
      });

      if (matchedUser) {
        return matchedUser.userName || matchedUser.name || matchedUser.email || matchedUser.UserName || matchedUser.Email || "Unknown user";
      }

      return "Unknown user";
    },
    getUserIconUrl(id) {
      const key = String(Math.min(Math.max(parseInt(id, 10) || 1, 1), 50)).padStart(2, "0");
      return `/images/avatars/${key}.svg`;
    },
    onAddFinding() {
      this.$router.push({ name: "FindingCreate" });
    },
    onRowClick(_event, { item }) {
      const findingId = item.findingId || item.FindingId;
      if (findingId) this.editFinding(findingId);
    },
    editFinding(id) {
      this.$router.push({ name: "FindingEdit", params: { id } });
    },
    confirmDeleteFinding(finding) {
      this.findingToDelete = finding;
      this.showDeleteConfirm = true;
    },
    async deleteFinding() {
      if (!this.findingToDelete) return;
      this.deletingFinding = true;
      try {
        const findingId = this.findingToDelete.findingId || this.findingToDelete.FindingId;
        if (!findingId) {
          this.$toast?.error?.("Finding ID not found");
          return;
        }
        await deleteItem(findingId);
        this.$toast?.success?.(`Finding "${this.findingToDelete.title}" deleted successfully`);
        this.showDeleteConfirm = false;
        this.findingToDelete = null;
        await this.fetchFindings();
      } catch (err) {
        const errorMsg = err?.response?.data?.error || err?.message || "Failed to delete finding";
        this.$toast?.error?.(errorMsg);
      } finally {
        this.deletingFinding = false;
      }
    },
    formatDateTime(dt) {
      if (!dt) return "";
      try {
        const date = new Date(dt);
        if (isNaN(date.getTime())) return "";
        return date.toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
      } catch {
        return "";
      }
    },
  },
  mounted() {
    this.loadFindingStatuses();
    this.loadZones();
    this.loadUsers();
    this.fetchFindings();
  },
};
</script>

<style scoped>
.findings-page-header {
  margin-bottom: 10px;
}

.findings-page-title {
  font-size: 1.9rem;
  font-weight: 700;
  line-height: 1.1;
  color: #223127;
}

.findings-page-subtitle {
  font-size: 0.98rem;
  color: #667085;
}

.findings-filters {
  padding: 16px 18px;
  border-radius: 18px;
  border: 1px solid #e8edf0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbf9 100%);
}

.findings-grid-card {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid #e8edf0;
}

.findings-table :deep(.v-data-table__tr) {
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.findings-table :deep(.v-data-table__tr:hover) {
  background-color: rgba(19, 119, 82, 0.05) !important;
}
.findings-table :deep(.v-data-table__tr:active) {
  background-color: rgba(19, 119, 82, 0.1) !important;
}
.findings-table :deep(thead th) {
  font-weight: 700 !important;
  color: #475467 !important;
  background: #fcfcfd;
}
.finding-title-link {
  color: #1565c0;
  font-weight: 500;
}
.submitted-by-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.survey-type-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.survey-type-text {
  color: #344054;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.submitted-by-avatar {
  flex-shrink: 0;
}
.submitted-by-text {
  font-weight: 500;
  color: #344054;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.task-badge-btn {
  padding: 0;
  min-width: 32px !important;
}
.task-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border-radius: 50%;
  background-color: #d32f2f;
  color: white;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}
.task-badge-btn:hover .task-badge {
  background-color: #b71c1c;
}
.task-badge--zero .task-badge {
  background-color: #bdbdbd;
}
.task-badge--zero:hover .task-badge {
  background-color: #9e9e9e;
}
</style>
